import React from 'react';
import { Button, Modal, ModalBody, ModalFooter } from '@/Components/ui';

export const VILLAGE_SYSTEM_NOTICE_TITLE = '시스템 사용료 결제 방식 변경 및 시스템 업데이트 안내';

interface VillageSystemNoticeModalProps {
  isOpen: boolean;
  onConfirm: () => void;
}

const BulletList = ({ children }: { children: React.ReactNode }) => (
  <ul className="mb-0 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
    {children}
  </ul>
);

export default function VillageSystemNoticeModal({ isOpen, onConfirm }: VillageSystemNoticeModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onConfirm}
      title={VILLAGE_SYSTEM_NOTICE_TITLE}
      size="3xl"
      variant="dialog"
      backdrop="static"
      centered
      scrollable
    >
      <ModalBody className="space-y-8 bg-slate-50/60 px-4 py-5 sm:px-6">
        <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
          <p className="mb-0 text-sm leading-6 text-slate-800">안녕하세요.</p>
          <p className="mb-0 text-sm leading-6 text-slate-700">
            보다 안정적인 시스템 운영과 지속적인 서비스 제공을 위해 시스템 사용료 결제 방식이 기존 후불제에서 선불제로 변경됩니다.
          </p>
        </section>

        <section aria-labelledby="village-system-payment-title" className="space-y-5 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
          <div>
            <h2 id="village-system-payment-title" className="mb-3 text-lg font-semibold text-slate-900">
              시스템 사용료 결제 방식 변경
            </h2>
            <BulletList>
              <li><strong className="font-semibold text-slate-900">결제방식 :</strong> 후불 → 선불</li>
              <li><strong className="font-semibold text-slate-900">청구일 :</strong> 매월 1일</li>
            </BulletList>
          </div>

          <p className="mb-0 rounded-md bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-950">
            관리자 모드에 결제 안내가 표시되며, 미입금 시 관리자 권한이 제한될 수 있습니다.
          </p>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-3 text-base font-semibold text-slate-900">8월 납부 금액</h3>
            <BulletList>
              <li>시스템 사용료 : 825,000원 (부가세 포함)</li>
              <li>시스템 기능 유료 업데이트 비용 : 330,000원 (부가세 포함)</li>
            </BulletList>
            <div className="mt-4 border-t border-slate-200 pt-3 text-base font-semibold text-slate-950">
              총 입금금액 : 1,155,000원
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-base font-semibold text-slate-900">입금계좌</h3>
            <BulletList>
              <li>우리은행 1002-167-349208</li>
              <li>예금주 : 서상후</li>
            </BulletList>
          </div>

          <div className="space-y-2 text-sm leading-6 text-slate-600">
            <p className="mb-0">※ 세금계산서는 매월 말일 발급 예정입니다.</p>
            <p className="mb-0">
              ※ 시스템 기능 유료 업데이트 비용은 정기적으로 청구되는 비용이 아닙니다. 신규 기능 개발이나 시스템 고도화 등 실제 업데이트가 진행된 경우에만 별도로 청구됩니다.
            </p>
          </div>

          <div>
            <h3 className="mb-3 text-base font-semibold text-slate-900">미입금 시 이용 제한</h3>
            <BulletList>
              <li>미입금 시 다음 날부터 관리자 페이지 접속이 제한될 수 있습니다.</li>
              <li>관리자 접속 제한 후 7일이 경과할 때까지 미입금 상태가 유지되면 사용자 페이지 접속도 제한될 수 있으니 기한 내 입금을 부탁드립니다.</li>
            </BulletList>
          </div>
        </section>

        <section aria-labelledby="village-system-update-title" className="space-y-6 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
          <div className="space-y-3">
            <h2 id="village-system-update-title" className="mb-0 text-lg font-semibold text-slate-900">
              이번 시스템 업데이트 안내
            </h2>
            <p className="mb-0 text-sm leading-6 text-slate-700">
              이번 업데이트에서는 알림 시스템을 전면 개편하여 고객 안내와 예약 관리 기능을 더욱 편리하게 사용할 수 있도록 개선하였습니다.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="mb-0 text-base font-semibold text-slate-900">1. 통합 알림센터 신설</h3>
            <p className="mb-0 text-sm leading-6 text-slate-700">
              기존 <strong className="font-semibold text-slate-900">알림 &amp; 메시지 메뉴를 '통합 알림센터'</strong>로 개편하였습니다.
            </p>
            <p className="mb-0 text-sm leading-6 text-slate-700">이제 아래 기능을 하나의 화면에서 관리할 수 있습니다.</p>
            <BulletList>
              <li>통합 발송</li>
              <li>발송 내역</li>
              <li>시스템 메시지 설정</li>
              <li>채널 설정</li>
            </BulletList>
          </div>

          <div className="space-y-3">
            <h3 className="mb-0 text-base font-semibold text-slate-900">2. 지점별 알림 설정 기능 추가</h3>
            <p className="mb-0 text-sm leading-6 text-slate-700">각 지점에서 다음 항목을 개별적으로 설정할 수 있습니다.</p>
            <BulletList>
              <li>결제 관련 알림</li>
              <li>이용 관련 알림</li>
              <li>지점 공지</li>
              <li>마케팅 알림</li>
              <li>필수 안내</li>
            </BulletList>
            <p className="mb-0 text-sm leading-6 text-slate-700">또한 항목별로 다음 발송 채널의 사용 여부를 설정할 수 있습니다.</p>
            <BulletList>
              <li>Web Push</li>
              <li>App Push</li>
              <li>카카오 알림톡</li>
            </BulletList>
          </div>

          <div className="space-y-3">
            <h3 className="mb-0 text-base font-semibold text-slate-900">3. 통합 발송 기능 강화</h3>
            <p className="mb-0 text-sm leading-6 text-slate-700">통합 발송 시 수신자별로 발송 채널을 선택하여 메시지를 보낼 수 있습니다.</p>
            <BulletList>
              <li>Web Push</li>
              <li>App Push</li>
              <li>카카오 알림톡</li>
            </BulletList>
            <p className="mb-0 text-sm leading-6 text-slate-700">카카오 알림톡은 아래 조건이 모두 충족된 경우에만 발송됩니다.</p>
            <BulletList>
              <li>지점 채널 활성화</li>
              <li>발신 프로필 등록</li>
              <li>승인된 알림톡 템플릿 등록</li>
              <li>수신자 전화번호 등록</li>
            </BulletList>
          </div>

          <div className="space-y-3 border-t border-slate-200 pt-5 text-sm leading-6 text-slate-700">
            <p className="mb-0">
              앞으로도 예약 시스템과 고객 관리 기능을 지속적으로 고도화하여 더욱 안정적이고 편리한 서비스를 제공할 수 있도록 최선을 다하겠습니다.
            </p>
            <p className="mb-0">감사합니다.</p>
          </div>
        </section>
      </ModalBody>
      <ModalFooter className="bg-white">
        <Button color="primary" onClick={onConfirm}>확인</Button>
      </ModalFooter>
    </Modal>
  );
}
